import { createContext, useContext, useState } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';

import { FallbackPage } from './FallbackPage';
import { Meta } from './Meta/Meta';
import { WistiaControlScript } from './WistiaVideo/WistiaControlScript';
import { UniversalLayout } from './UniversalLayout/UniversalLayout';
import type { AlertBanner, PageContent } from '../builder';

import { PageBuilderComponent } from './PageBuilderComponent';
import { TrackRouting } from '../observability';

const Survey = dynamic<any | never>(() =>
  import('./Survey/Survey').then((mod) => mod.SurveyContainer),
);

export type PageData = PageContent['data'] & {
  alertBanner: AlertBanner & {
    /**
     * If the alert banner has a body with data binding, then it must be parsed
     * in order for the asyncData values to be injected.
     * parsedBody stores that value. We don't overwrite alertBanner.body with the parsed value
     * because we need to keep the original in the event that the asyncData values change.
     */
    parsedBody?: string;
  };
};

type Props = {
  content: PageContent;
};

type PageContextValues = {
  id?: string;
  variations?: PageContent['variations'];
};

const PageContext = createContext<PageContextValues>({});

export const usePageContext = () => useContext(PageContext);

export function Page({ content }: Props) {
  const router = useRouter();
  const [pageData, setPageData] = useState<PageData>(content?.data as PageData);

  // https://nextjs.org/docs/basic-features/data-fetching#fallback-pages
  if (router.isFallback) {
    return <FallbackPage />;
  }

  const {
    alertBanner,
    articleStructuredData,
    description,
    openGraphImageUrl,
    openToSearchEngines,
    showSurvey,
    title,
    universalFooter,
    universalLayout,
  } = pageData || {};

  return (
    <PageContext.Provider
      value={{
        id: content.id,
        variations: content.variations,
      }}
    >
      <UniversalLayout
        alertBanner={{
          ...alertBanner,
          body: alertBanner?.parsedBody || alertBanner?.body,
        }}
        hideFooter={universalLayout?.hideFooter}
        hideHeader={universalLayout?.hideHeader}
        shouldRenderAlphabetNav={universalFooter?.shouldRenderAlphabetNav}
        shouldRenderSeoLinks={universalFooter?.shouldRenderSeoLinks}
      >
        <TrackRouting />
        <Meta
          articleStructuredData={articleStructuredData}
          description={description}
          openGraphImageUrl={openGraphImageUrl}
          openToSearchEngines={openToSearchEngines}
          title={title}
        />
        <WistiaControlScript />
        <PageBuilderComponent content={content} setPageData={setPageData} />
        {showSurvey && <Survey />}
      </UniversalLayout>
    </PageContext.Provider>
  );
}
